import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';

export interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  data: Record<string, any>;
  readAt: string | null;
  createdAt: string;
}

interface NotificationsResponse {
  notifications: Notification[];
  unreadCount: number;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface UseNotificationsOptions {
  unreadOnly?: boolean;
  limit?: number;
  refetchInterval?: number | false;
  enabled?: boolean;
}

export function useNotifications(options: UseNotificationsOptions = {}) {
  const { unreadOnly = false, limit = 10, refetchInterval = 30000, enabled = true } = options;
  const { isAuthenticated } = useAuthStore();
  const queryClient = useQueryClient();

  const queryKey = ['notifications', { unreadOnly, limit }];

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (unreadOnly) params.set('unreadOnly', 'true');
      params.set('limit', String(limit));
      const response = await api.get(`/users/notifications?${params}`);
      return (response as any).data as NotificationsResponse;
    },
    enabled: enabled && isAuthenticated,
    refetchInterval,
    staleTime: 10000,
  });

  const markAsReadMutation = useMutation({
    mutationFn: (notificationId: string) =>
      api.put(`/users/notifications/${notificationId}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: () => api.put('/users/notifications/read-all'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  return {
    notifications: data?.notifications || [],
    unreadCount: data?.unreadCount || 0,
    pagination: data?.pagination,
    isLoading,
    error,
    refetch,
    markAsRead: markAsReadMutation.mutate,
    markAllAsRead: markAllAsReadMutation.mutate,
    isMarkingRead: markAsReadMutation.isPending,
    isMarkingAllRead: markAllAsReadMutation.isPending,
  };
}

// Helper to get notification icon and color based on type
export function getNotificationStyle(type: string): { color: string; icon: string } {
  switch (type) {
    case 'order_completed':
      return { color: 'text-success', icon: '✓' };
    case 'order_failed':
      return { color: 'text-error', icon: '✕' };
    case 'payment_received':
      return { color: 'text-success', icon: '$' };
    case 'product_delivered':
      return { color: 'text-success', icon: '📦' };
    case 'new_support_ticket':
      return { color: 'text-gold', icon: '🎫' };
    case 'ticket_reply':
      return { color: 'text-info', icon: '💬' };
    case 'ticket_resolved':
      return { color: 'text-success', icon: '✓' };
    case 'seller_payout':
      return { color: 'text-success', icon: '💰' };
    case 'product_review':
      return { color: 'text-gold', icon: '⭐' };
    case 'system':
      return { color: 'text-text-secondary', icon: 'ℹ' };
    default:
      return { color: 'text-text-muted', icon: '•' };
  }
}

// Helper to get notification link based on type and data
export function getNotificationLink(type: string, data: Record<string, any>): string | null {
  switch (type) {
    case 'order_completed':
    case 'order_failed':
    case 'payment_received':
    case 'product_delivered':
      return data.orderId ? `/dashboard/orders/${data.orderId}` : null;
    case 'new_support_ticket':
    case 'ticket_reply':
    case 'ticket_resolved':
      return data.ticketId ? `/support/${data.ticketId}` : null;
    case 'seller_payout':
      return '/dashboard/seller/payouts';
    case 'product_review':
      return data.productId ? `/products/${data.productId}` : null;
    default:
      return null;
  }
}

// Format relative time
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}
